<template>
    <b-modal
        id="opening-cash-modal"
        ref="modal"
        hide-header-close
        no-close-on-backdrop
        no-close-on-esc
        hide-footer
        centered
    >
        <template #modal-title>
            <span class="font-weight-bold">Open Cash Session</span>
        </template>

        <div class="px-2 pb-2">
            <p class="text-muted mb-3">
                Enter the opening cash float before starting transactions.
            </p>

            <form @submit.prevent="submit">
                <div class="form-group">
                    <label class="font-weight-bold">Opening Cash Amount <span class="text-danger">*</span></label>
                    <div class="input-group">
                        <div class="input-group-prepend">
                            <span class="input-group-text">{{ currencySymbol }}</span>
                        </div>
                        <input
                            ref="amountInput"
                            type="number"
                            class="form-control form-control-lg"
                            v-model.number="form.opening_amount"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            required
                            :disabled="submitting"
                        />
                    </div>
                </div>

                <div class="form-group">
                    <label>Notes <small class="text-muted">(optional)</small></label>
                    <textarea
                        class="form-control"
                        v-model="form.notes"
                        rows="2"
                        maxlength="500"
                        placeholder="e.g. Float from manager, petty cash..."
                        :disabled="submitting"
                    ></textarea>
                </div>

                <div v-if="error" class="alert alert-danger py-2 mb-3">
                    {{ error }}
                </div>

                <div class="d-flex justify-content-end">
                    <button
                        type="submit"
                        class="btn btn-primary px-4"
                        :disabled="submitting || form.opening_amount === null || form.opening_amount < 0"
                    >
                        <span v-if="submitting">Opening...</span>
                        <span v-else>Open Session</span>
                    </button>
                </div>
            </form>
        </div>
    </b-modal>
</template>

<script>
export default {
    name: 'OpeningCashModal',

    data() {
        return {
            form: {
                opening_amount: null,
                notes: '',
            },
            submitting: false,
            error: null,
            pendingRetry: null,
        };
    },

    computed: {
        currencySymbol() {
            try {
                const ud = this.$store.state.userDetails;
                return ud?.company?.companysettings?.currency || 'KES';
            } catch {
                return 'KES';
            }
        },
    },

    mounted() {
        // Listen for the cash session gate event emitted by the Axios interceptor
        this.$root.$on('cash-session-required', this.onRequired);
    },

    beforeDestroy() {
        this.$root.$off('cash-session-required', this.onRequired);
    },

    methods: {
        onRequired({ retry, cancel }) {
            this.pendingRetry = retry;
            this.pendingCancel = cancel;
            this.form.opening_amount = null;
            this.form.notes = '';
            this.error = null;
            this.$refs.modal.show();
            this.$nextTick(() => {
                this.$refs.amountInput?.focus();
            });
        },

        async submit() {
            this.error = null;
            this.submitting = true;

            try {
                const { data } = await axios.post('/api/cash-sessions', {
                    opening_amount: this.form.opening_amount,
                    notes: this.form.notes || null,
                });

                this.$store.commit('setCashSession', data);
                this.$refs.modal.hide();

                // Resume the blocked financial request
                if (this.pendingRetry) {
                    this.pendingRetry();
                    this.pendingRetry = null;
                }

                this.$toasts.push({ type: 'success', message: 'Cash session opened.' });
            } catch (err) {
                if (err.response?.status === 409) {
                    // Already has a session (race condition) — just retry
                    this.$refs.modal.hide();
                    if (this.pendingRetry) {
                        this.pendingRetry();
                        this.pendingRetry = null;
                    }
                } else {
                    this.error = err.response?.data?.message || 'Failed to open session. Please try again.';
                }
            } finally {
                this.submitting = false;
            }
        },
    },
};
</script>
